import React, {useEffect, useRef, useState} from 'react';
import {useLocation, useNavigate} from 'react-router-dom'; // Logic Added
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Accordion from 'react-bootstrap/Accordion';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';
import Spinner from 'react-bootstrap/Spinner';
import Button from 'react-bootstrap/Button';
import Table from 'react-bootstrap/Table';
import axios from 'axios';
import errorMessages from '../errorMessages.js';
import {showToast as toast} from '../toastManager';
import {verifySdkPopup, describeVerifyFailure} from '../utils/verifySdkPopup';
// import errorMessages from '../errorMessages.js';
import {v4 as uuidv4} from 'uuid';
import Label from 'react-bootstrap/FormLabel';
import Breadcrumbs from '../components/Breadcrumbs';
import ABTestSection from '../components/abTesting/ABTestSection';
import { AB_CATEGORY, canUseAbTesting, experimentStatusLabel, experimentStatusTone, newAbTest, normalizeLandingPage } from '../utils/abTesting';
import '../ChatApp.css';
import '../Opportunites.css';

const FRONTEND_ENVIRONMENT = process.env.REACT_APP_VISUAL_EDITOR_ENV || 'dev';

// Snapshot of the campaign fields the Save button persists, used to keep Save
// disabled until something actually changes. targeting_and_content is
// intentionally excluded — variant delete auto-persists to /api/cux/delete-variant.
// status is left out: for an A/B campaign it follows the test and the server owns it.
const campaignSnapshot = (cd) => JSON.stringify({
    campaign_name:     cd?.campaign_name ?? '',
    sdk_verified:      !!cd?.sdk_verified,
    landing_page_urls: cd?.landing_page_urls ?? '',
    templates:         cd?.templates ?? null,
    // A/B settings Save persists. The server-owned lifecycle fields are excluded.
    ab_test: cd?.ab_test ? {
        arms: cd.ab_test.arms,
        traffic_split: cd.ab_test.traffic_split,
        goal_metric: cd.ab_test.goal_metric,
        goal_selectors: cd.ab_test.goal_selectors,
        hypothesis: cd.ab_test.hypothesis,
    } : null,
});


const getCampaignsFromStorage = () => {
    const storedCampaigns = sessionStorage.getItem('campaigns');
    if (storedCampaigns) {
        try {
            const parsed = JSON.parse(storedCampaigns);
            return Array.isArray(parsed) ? parsed : [];
        } catch (e) {
            console.error("Failed to parse campaigns from sessionStorage:", e);
            return [];
        }
    }
    return [];
};

const saveCampaignsToStorage = (campaigns) => {
    try {

        const validCampaigns = campaigns.filter(c => c && c.campaign_id);
        sessionStorage.setItem('campaigns', JSON.stringify(validCampaigns));
    } catch (e) {
        console.error("Failed to save campaigns to sessionStorage:", e);
        toast.error("Could not save campaign changes.");
    }
};


const ABTestingCampaign = ({ViewedPage, shop_domain, app_url, allowed_pages_json}) => {
    ViewedPage(28);
    const location = useLocation();
    const navigate = useNavigate();

    // No A/B Testing page access for this store (page ID 72): back to the campaign list.
    const abAllowed = canUseAbTesting(allowed_pages_json, shop_domain);
    useEffect(() => {
        if (!abAllowed) navigate('/campaign-summary', { replace: true });
    }, [abAllowed, navigate]);

    const initialallTargeting = location.state?.allTargeting;
    const initialCampaignData = location.state?.campaignData;
    console.log("initialCampaignData", initialCampaignData)
    const previousPage = location.state?.previousPage || 'campaign-summary'; // Track where we came from

    const [loading, setLoading] = useState(true);
    const [select_template_button_loading, setSelectTemplateButtonLoading] = useState(false);
    const [select_landing_page_button_loading, setSelectLandingPageButtonLoading] = useState(false);
    const [publish_btn_loading, setPublishBtnLoading] = useState(false);
    const [campaignData, setCampaignData] = useState(null);
    const [isEditingName, setIsEditingName] = useState(false);
    const campaignNameInputRef = useRef(null);
    const [sdkClientKey, setSdkClientKey] = useState(null);
    // SDK verification status — not a gate, just a non-blocking signal we
    // surface in a banner inside the landing-page accordion. Values:
    //   'idle'      → never verified (banner: prompt to verify)
    //   'verifying' → popup is open (banner: spinner)
    //   'verified'  → SDK confirmed on the page (banner hidden)
    //   'failed'    → last attempt failed (banner: error + retry)
    const [sdkVerifyState, setSdkVerifyState] = useState('idle');
    const [sdkVerifyError, setSdkVerifyError] = useState(null);
    // Collapsed by default — the "not verified" notice shows only a small heading
    // until the user expands it (auto-expands after a failed verify attempt).
    const [sdkNoticeOpen, setSdkNoticeOpen] = useState(false);
    // Baseline snapshot of the loaded campaign — Save stays disabled until
    // campaignSnapshot(campaignData) differs from this.
    const initialSnapshotRef = useRef(null);
    // New state: track if template has been selected
    const [templateSelected, setTemplateSelected] = useState(false);
    // New state: track if landing page has been selected
    const [landingPageSelected, setLandingPageSelected] = useState(false);
    const [landingPageValidationError, setLandingPageValidationError] = useState('');
    const [templatePreview, setTemplatePreview] = useState(null);
    const [templateRefreshLoading, setTemplateRefreshLoading] = useState(false);
    const [templatePolling, setTemplatePolling] = useState(false);
    const templatePollIntervalRef = useRef(null);
    const templatePollTimeoutRef = useRef(null);
    const [activeAccordionKeys, setActiveAccordionKeys] = useState([]);
    const lockedAccordionStyle = {
        pointerEvents: 'none',
        userSelect: 'none',
        opacity: 1
    };


    const pollTemplateOnce = async () => {
        if (!campaignData?.campaign_id) return false;
        try {
            const response = await axios.get(`${app_url}/api/store/personalized_campaigns/${campaignData.campaign_id}/template/poll`, {
                withCredentials: true,
                params: {
                    landing_page_url: campaignData?.landing_page_urls
                }
            });
            if (response?.data?.success && response?.data?.data) {
                applyTemplateUpdate(response.data.data, { showToast: false });
                toast.success('Template updated from Visual Editor.');
                return true;
            }
        } catch (error) {
            console.error('Polling template failed', error);
        }
        return false;
    };

    const startTemplatePolling = () => {
        if (templatePolling || !campaignData?.campaign_id) return;
        clearTemplatePolling(true);
        setTemplatePolling(true);
        // Interval: every 3s, Timeout: 90s
        templatePollIntervalRef.current = setInterval(async () => {
            const done = await pollTemplateOnce();
            if (done) {
                clearTemplatePolling();
            }
        }, 3000);
        templatePollTimeoutRef.current = setTimeout(() => {
            clearTemplatePolling();
            setTemplatePolling(false);
            toast.error('Template update timed out. You can still use Refresh Template.');
        }, 90000);
    };

    const LockedHeader = ({label, locked, reason, additionalContent=null}) => (
        <span className={locked ? '' : ''}>{label} {additionalContent && <><br /><i style={{fontSize: '10px'}}>{additionalContent}</i></>}</span>
    );

    const AccordionLockIcon = ({reason}) => (
        <span title={reason} className="d-inline-flex align-items-center" style={{fontSize: '0.9rem'}}>
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="black" className="bi bi-lock-fill" viewBox="0 0 16 16">
                <path d="M8 1a3 3 0 0 0-3 3v3h6V4a3 3 0 0 0-3-3zm4 6H4a1 1 0 0 0-1 1v6a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V8a1 1 0 0 0-1-1z"/>
            </svg>
        </span>
    );

    const normalizeTemplatePayload = (templatePayload) => {
        if (!templatePayload) return null;
        if (Array.isArray(templatePayload)) {
            return templatePayload[0] || null;
        }
        if (templatePayload.value && typeof templatePayload.value === 'object') {
            return templatePayload.value;
        }
        return templatePayload;
    };

    const getCustomizedSections = (templatePayload) => {
        const normalized = templatePayload?.customized_sections;
        return Array.isArray(normalized) ? normalized : [];
    };

    const isTemplatePayloadLocked = (templatePayload) => {
        if (!templatePayload) return false;
        if (templatePayload?.locked === true) return true;
        if (templatePayload?.value && typeof templatePayload.value === 'object' && templatePayload.value.locked === true) {
            return true;
        }

        const normalized = normalizeTemplatePayload(templatePayload);
        return Boolean(normalized && typeof normalized === 'object' && normalized.locked === true);
    };


    const clearTemplatePolling = (silent = false) => {
        if (templatePollIntervalRef.current) {
            clearInterval(templatePollIntervalRef.current);
            templatePollIntervalRef.current = null;
        }
        if (templatePollTimeoutRef.current) {
            clearTimeout(templatePollTimeoutRef.current);
            templatePollTimeoutRef.current = null;
        }
        if (!silent) {
            setTemplatePolling(false);
        }
    };

    
    // Logic Added: Status options

    const [status, setStatus] = useState({
        value: 'draft',
        label: 'Draft'
    });


    const applyTemplateUpdate = (refreshedCampaign, { showToast = true } = {}) => {
        const refreshedTemplate = normalizeTemplatePayload(refreshedCampaign?.templates);
        if (refreshedTemplate) {
            setTemplatePreview(refreshedTemplate);
            setTemplateSelected(false);
            setCampaignData({...refreshedCampaign, status: status.value || refreshedCampaign.status});
            if (showToast) {
                toast.success('Template refreshed. Review the updates below.');
            }
        } else if (showToast) {
            toast.error('No template data available yet. Try launching the editor first.');
        }
    };

    const canLaunchVisualEditor = Boolean(
        landingPageSelected &&
        campaignData?.landing_page_urls &&
        campaignData.landing_page_urls.trim()
    );

    const templateAccordionLocked = isTemplatePayloadLocked(campaignData?.templates);

    useEffect(() => {
        console.log("PersonalizationCampaign received initial data:", initialCampaignData);
        if (initialCampaignData) {
            const ensuredId = initialCampaignData.campaign_id || uuidv4();

            const dataToSet = {
                ...initialCampaignData,
                campaign_id: ensuredId,
                campaign_name: initialCampaignData.campaign_name || 'Untitled Campaign',
                status: initialCampaignData.status || 'draft',
                category: AB_CATEGORY,
                landing_page_urls: initialCampaignData.landing_page_urls || "",
                templates: initialCampaignData.templates || {},
                sections_rearrangement: initialCampaignData.sections_rearrangement || [],
                  ab_test: initialCampaignData.ab_test || newAbTest(),
                targeting_and_content: (initialCampaignData.targeting_and_content || []).map(item => ({
                    ...item,
                    id: item.id || uuidv4(),
                    lpPreview: item.lpPreview || item.preview_url || ''
                }))
            };
            console.log("Setting campaign data state:", dataToSet);
            setCampaignData(dataToSet);
            // Baseline for the dirty-check that gates the Save button.
            initialSnapshotRef.current = campaignSnapshot(dataToSet);
            // Restore a previously verified SDK status so the notice stays hidden.
            if (dataToSet.sdk_verified) setSdkVerifyState('verified');
            const savedStatus = (dataToSet.status || 'draft').toLowerCase();
            setStatus({
                value: savedStatus,
                label: savedStatus.charAt(0).toUpperCase() + savedStatus.slice(1)
            });
            if (dataToSet.templates) {
                setTemplatePreview(normalizeTemplatePayload(dataToSet.templates));
            }

            const hasTemplateLock = isTemplatePayloadLocked(dataToSet.templates);
            // If landing page and templates are already set in initial data, mark template as selected (lock fields)
            if (dataToSet.landing_page_urls && dataToSet.landing_page_urls.trim() &&
                dataToSet.templates && Object.keys(dataToSet.templates).length > 0) {
                setTemplateSelected(true);
                setLandingPageSelected(true);
            }
            if (hasTemplateLock) {
                setTemplateSelected(true);
            }

            setLoading(false);
        } else {
            navigate('/campaign-summary');
        }
    }, [initialCampaignData, navigate]);

    // Keep accordions closed for locked sections
    useEffect(() => {
        setActiveAccordionKeys((prev) => {
            let next = prev;
            if (landingPageSelected) {
                next = next.filter(key => key !== "0");
            }
            if (templateAccordionLocked) {
                next = next.filter(key => key !== "1");
            }
            return next;
        });
    }, [landingPageSelected, templateAccordionLocked]);

    const handleCampaignNameEdit = () => {
        setIsEditingName(true);
        setTimeout(() => {
            campaignNameInputRef.current?.focus();
        }, 0);
    };

    const handleCampaignNameChange = (e) => {
        setCampaignData(prev => ({...prev, campaign_name: e.target.value}));
    };

    const handleCampaignNameBlur = () => {
        setIsEditingName(false);
        if (!campaignData?.campaign_name?.trim()) {
            setCampaignData(prev => ({...prev, campaign_name: 'Untitled Campaign'}));
        }
        saveData();
    };

    const handleCampaignNameKeyDown = (e) => {
        if (e.key === 'Enter') {
            handleCampaignNameBlur();
        }
    };

    const getSdkClientKey = async () => {
        if (sdkClientKey) {
            return sdkClientKey;
        }

        try {
            const response = await axios.post(`${app_url}/api/cux/connection-sdk-script`, {
                language: 'html'
            }, {withCredentials: true});

            if (response?.data?.clientKey) {
                setSdkClientKey(response.data.clientKey);
                return response.data.clientKey;
            }

            toast.error('Unable to fetch SDK client key.');
            return null;
        } catch (error) {
            console.error('Error fetching SDK client key:', error);
            toast.error('Failed to fetch SDK client key.');
            return null;
        }
    };

    // Triggered by the "Verify connection" button in the in-accordion
    // banner. Non-blocking: state updates drive the banner UI, but the
    // rest of the campaign flow runs regardless of result.
    const handleSdkVerifyClick = async () => {
        const url = campaignData?.landing_page_urls?.trim();
        if (!url) {
            toast.error('Please add a landing page URL first.');
            return;
        }
        if (sdkVerifyState === 'verifying') return;

        setSdkVerifyState('verifying');
        setSdkVerifyError(null);

        const clientKey = await getSdkClientKey();
        if (!clientKey) {
            setSdkVerifyState('failed');
            setSdkVerifyError('Could not fetch SDK client key.');
            return;
        }

        let startData;
        try {
            const startResponse = await axios.post(`${app_url}/api/store/check-sdk-script`, {
                url,
                clientKey
            }, {withCredentials: true});
            startData = startResponse?.data;
        } catch (error) {
            console.error('Error starting SDK verification:', error);
            setSdkVerifyState('failed');
            setSdkVerifyError('Could not start verification. Please try again.');
            return;
        }

        if (!startData || startData.mode !== 'popup' || !startData.popupUrl || !startData.pollUrl) {
            setSdkVerifyState('failed');
            setSdkVerifyError(startData?.message || 'Could not start SDK verification.');
            return;
        }

        if (toast.info) toast.info('Opening your landing page in a new tab to verify the SDK…');

        const result = await verifySdkPopup({
            popupUrl: startData.popupUrl,
            pollUrl: startData.pollUrl,
            token: startData.token,
            expiresInMs: startData.expiresInMs
        });

        if (result.ok && result.inHead) {
            setSdkVerifyState('verified');
            setSdkVerifyError(null);
            // Persist the verified status on the campaign so we don't prompt again —
            // rides in campaignData to sessionStorage now and to the server on publish.
            setCampaignData(prev => {
                const updated = { ...prev, sdk_verified: true, sdk_verified_at: new Date().toISOString() };
                saveData(null, updated);
                return updated;
            });
            return;
        }
        if (result.ok && !result.inHead) {
            setSdkVerifyState('failed');
            setSdkVerifyError('SDK is loaded but not inside the <head> tag. Please move the script tag into <head>.');
            setSdkNoticeOpen(true);
            return;
        }
        setSdkVerifyState('failed');
        setSdkVerifyError(describeVerifyFailure(result));
        setSdkNoticeOpen(true);
    };

    const base64UrlEncode = (input) => {
        try {
            const base64 = window.btoa(unescape(encodeURIComponent(input)));
            return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
        } catch (error) {
            console.error('Error encoding launch payload:', error);
            return null;
        }
    };

    const buildLaunchEditorToken = () => {
        if (!campaignData?.landing_page_urls) {
            return null;
        }

        const resolvedTemplate = templatePreview || normalizeTemplatePayload(campaignData?.templates);
        const templateName = resolvedTemplate?.name || resolvedTemplate?.label || resolvedTemplate?.value?.name || '';
        const payload = {
            client_name: shop_domain?.replace(".myshopify.com", "") || shop_domain || '',
            environment: FRONTEND_ENVIRONMENT,
            template_name: templateName,
            landing_page_url: campaignData.landing_page_urls.trim(),
            campaign_id: campaignData?.campaign_id || uuidv4(),
            campaign_name: campaignData?.campaign_name || 'Untitled Campaign',
            category: campaignData?.category || 'Uncategorized'
        };

        return base64UrlEncode(JSON.stringify(payload));
    };

    const handleLaunchVisualEditor = () => {
        if (!landingPageSelected || !campaignData?.landing_page_urls || !campaignData.landing_page_urls.trim()) {
            toast.error('Please select a landing page first.');
            return;
        }

        const token = buildLaunchEditorToken();
        if (!token) {
            toast.error('Failed to build launch payload.');
            return;
        }

        try {
            const launchUrl = new URL(campaignData.landing_page_urls.trim());
            launchUrl.searchParams.set('sxi_auth_token', token);
            window.open(launchUrl.toString(), '_blank', 'noopener,noreferrer');
            startTemplatePolling();
        } catch (error) {
            console.error('Error launching Visual Editor:', error);
            toast.error('Invalid landing page URL.');
        }
    };

    const validateLandingPageUrl = (value) => {
        if (!value || !value.trim()) {
            return '';
        }

        try {
            const parsed = new URL(value.trim());
            if (!['http:', 'https:'].includes(parsed.protocol)) {
                return 'Enter a valid URL that starts with http or https.';
            }

            const hostname = parsed.hostname;
            const isLocalhost = hostname === 'localhost' || hostname.endsWith('.localhost');
            if (!isLocalhost) {
                if (!hostname || hostname.length < 3 || !hostname.includes('.')) {
                    return 'Enter a valid domain (e.g., https://example.com).';
                }
            }

            return '';
        } catch (error) {
            return 'Enter a valid landing page URL.';
        }
    };

    const updateLandingPageSelection = (value) => {
        const normalizedValue = value || '';
        console.log("handleLandingPageChange", normalizedValue);
        const validationMessage = validateLandingPageUrl(normalizedValue);
        setLandingPageValidationError(validationMessage);

        setCampaignData(prev => ({
            ...prev,
            landing_page_urls: normalizedValue,
            templates: []
        }));

        setLandingPageSelected(false);
        setTemplateSelected(false);
        setTemplatePreview(null);
    };

    const handleLandingPageInputChange = (event) => {
        updateLandingPageSelection(event.target.value);
    };

    const handleAccordionSelect = (eventKey) => {
        if (eventKey === "0" && landingPageSelected) return;
        if (eventKey === "1" && templateAccordionLocked) return;
        setActiveAccordionKeys((prev) => {
            if (prev.includes(eventKey)) {
                return prev.filter((key) => key !== eventKey);
            }
            return [...prev, eventKey];
        });
    };

    // Handler for landing page select button
    const handleLandingPageSelect = async () => {
        if (!campaignData.landing_page_urls || !campaignData.landing_page_urls.trim()) {
            toast.error('Please select a landing page URL first');
            return;
        }

        setSelectLandingPageButtonLoading(true);

        try{
            // Stored normalized (origin + path): flags, worker mapping and experiment all match this exact string.
            const trimmedLandingPageUrl = normalizeLandingPage(campaignData.landing_page_urls);
            if (!trimmedLandingPageUrl) {
                setSelectLandingPageButtonLoading(false);
                toast.error('Please enter a valid landing page URL');
                return;
            }
            // SDK presence is now a non-blocking check surfaced via the in-accordion
            // banner. The user can verify on demand via that button.
            let client_name_from_url = getNameFromUrl(cleanUrl(trimmedLandingPageUrl))
            let experimentName = shop_domain.replace(".myshopify.com", "") + ( client_name_from_url ? " - " + client_name_from_url : "")
            let featureFlagName = shop_domain.replace(".myshopify.com", "") + ( client_name_from_url ? " - " + client_name_from_url : "")
            const response = await axios.post(`${app_url}/api/cux/experiments`, {
                experimentName: experimentName,
                featureFlagName: featureFlagName,
                landingPageUrl: cleanUrl(trimmedLandingPageUrl)
            }, {withCredentials: true});

            console.log(response)
    
            if(response && response.data && response.data.success){
            }
            else if(response && response.data && !response.data.success && response.data.show_message === true){
                toast.error(response?.data?.message ? response?.data?.message : "Configuration not applied");
            }
            setSelectLandingPageButtonLoading(false);
            
            setLandingPageSelected(true);
            saveData(null, { ...campaignData, landing_page_urls: trimmedLandingPageUrl }); // store the normalized form
            toast.success('Landing page selected successfully');
        }
        catch(err){
            setSelectLandingPageButtonLoading(false);
            console.error('Error selecting landing page:', err);
            toast.error('Failed to select landing page');
        }

    };

    const handleTemplateRefresh = async () => {
        if (!campaignData?.campaign_id) {
            toast.error('Campaign information is missing.');
            return;
        }
        if (!landingPageSelected) {
            toast.error('Please select a landing page first.');
            return;
        }

        setTemplateRefreshLoading(true);
        try {
            const response = await axios.get(`${app_url}/api/store/personalized_campaigns/${campaignData.campaign_id}/template`, {
                withCredentials: true,
                params: {
                    landing_page_url: campaignData?.landing_page_urls
                }
            });
            if (response?.data?.success) {
                const refreshedCampaign = response?.data?.data || {};
                console.log("refreshedCampaign", refreshedCampaign)
                applyTemplateUpdate(refreshedCampaign);
            } else {
                toast.error(response?.data?.message || 'Failed to refresh template.');
            }
        } catch (error) {
            console.error('Error refreshing template:', error);
            toast.error('Failed to refresh template.');
        } finally {
            setTemplateRefreshLoading(false);
        }
    };

    // Handler for template select button
    const handleTemplateSelect = async () => {
        setSelectTemplateButtonLoading(true);
        try {
            if (!landingPageSelected || !campaignData?.landing_page_urls?.trim()) {
                toast.error('Please select a landing page first');
                setSelectTemplateButtonLoading(false);
                return;
            }
            if (!templatePreview) {
                toast.error('Please refresh to load the latest template before selecting.');
                setSelectTemplateButtonLoading(false);
                return;
            }


            const lockedTemplatePayload = {
                ...templatePreview,
                locked: true
            };
            const cleanedLpUrl = campaignData.landing_page_urls;
            const apiUrl = process.env.REACT_APP_VERSION === 'v2' ? `${app_url}/api/cux/v2/templates` : `${app_url}/api/cux/templates`;
            const response = await axios.post(apiUrl, {
                landingPageUrl: cleanedLpUrl,
                template: lockedTemplatePayload,
                campaign_id: campaignData?.campaign_id,
                campaign_name: campaignData?.campaign_name || 'Untitled Campaign'
            }, {withCredentials: true});

            console.log(response);

            if (response && response.data && response.data.success) {
                // Lock template selection and persist
                setTemplateSelected(true);
                const updatedCampaign = {
                    ...campaignData,
                    templates: lockedTemplatePayload
                };
                saveData(null, updatedCampaign);
                toast.success('Template selected successfully');
            } else if (response && response.data && !response.data.success && response.data.show_message) {
                toast.error(response?.data?.message ? response?.data?.message : 'Configuration not applied');
            }
            setSelectTemplateButtonLoading(false);
        } catch (error) {
            console.error('Error selecting Template:', error);
            toast.error('Failed to select Template');
            setSelectTemplateButtonLoading(false);
        }
    };

    // Handler for template select
    const handleTemplateChange = (value) => {
        setCampaignData(prev => ({
            ...prev,
            templates: value ? value : {},
            // Add sections_rearrangement for all templates
            ...(value && value.sections_rearrangement && { sections_rearrangement: value.sections_rearrangement })
        }));
        // Do not saveData here, only on select button click
    };




    // --- Save/Publish ---
    const saveData = (newStatus = null, overrideCampaign = null) => {
        const sourceCampaign = overrideCampaign || campaignData;
        if (!sourceCampaign || !sourceCampaign.campaign_id) {
            console.error("Cannot save: Campaign data or ID is missing.", sourceCampaign);
            toast.error("Cannot save: Campaign ID is missing.");
            return false;
        }

        let campaigns = getCampaignsFromStorage();
        const campaignIndex = campaigns.findIndex(c => c.campaign_id === sourceCampaign.campaign_id);

        // Prepare data, ensuring lpPreview is saved correctly
        const dataToSave = {
            ...sourceCampaign
        };

        if (newStatus) {
            dataToSave.status = newStatus;
        }

        if (campaignIndex > -1) {
            campaigns[campaignIndex] = dataToSave;
            console.log("Updated campaign in storage:", dataToSave);
        } else {
            campaigns.push(dataToSave);
            console.log("Added new campaign to storage:", dataToSave);
        }
        saveCampaignsToStorage(campaigns);
        // Update local state if status was changed externally (like via publish)
        if (!overrideCampaign && newStatus && newStatus !== (campaignData?.status)) {
            setCampaignData(dataToSave);
        } else if (overrideCampaign) {
            setCampaignData(dataToSave);
        }
        return true;
    };

    const handlePublish = async () => {
        if (!landingPageSelected || !campaignData?.landing_page_urls?.trim()) {
            toast.error('Please select a landing page first.');
            return;
        }
        setPublishBtnLoading(true);
        try {
            const response = await axios.post(
                `${app_url}/api/cux/publish-campaign`,
                {campaignData},
                {withCredentials: true}
            );

            if (response.data.success) {
                toast.success('Campaign saved successfully!');
                initialSnapshotRef.current = campaignSnapshot(campaignData);
                // Navigate back to summary page
                // navigate('/campaign-summary');
            } else {
                throw new Error(response.data.message || 'Failed to publish campaign');
            }
        } catch (error) {
            console.error('Error publishing campaign:', error);
            toast.error(errorMessages["PUBLISH_ERROR"]["title"]);
        } finally {
            setPublishBtnLoading(false);
        }
    };
    // --- End Logic Added ---




    useEffect(() => {
        return () => {
            clearTemplatePolling(true);
        };
    }, [campaignData]);


    const handleBack = () => {
        if (previousPage === 'category') {
            navigate('/category', {state: {campaignData: campaignData, allTargeting: initialallTargeting}});
        } else {
            navigate('/campaign-summary');
        }
    };

    // Render Loading state
    if (loading || !campaignData) {
        return (
            <div className='main-container' style={{position: "relative"}}>
                <div className="text-center mt-5">
                    <Spinner animation="border" role="status">
                        <span className="visually-hidden">Loading Campaign...</span>
                    </Spinner>
                </div>
            </div>
        );
    }


    return (
        <div className='main-container'>
            {!shop_domain ? (
                <div className='go_to_shopify_admin_div'>
                    <h5 style={{display: 'inline-block'}}>!! Go to Shopify Admin and open our app !!</h5>
                </div>
            ) : (
                <>
                    {loading ? (
                        <div className="text-center mt-5">
                            <Spinner animation="border" role="status">
                                <span className="visually-hidden">Loading Campaign...</span>
                            </Spinner>
                        </div>
                    ) : (
                        <>
                            {/* <div className="campaign-top-nav">
                                <Breadcrumbs campaignData={campaignData}/>
                            </div> */}

                            <Container fluid className="mx-0">
                                <Row className="mb-4">
                                    <Col className="d-flex align-items-center justify-content-between" style={{'paddingLeft':'5px', 'paddingRight':'5px'}}>
                                        <div className="custom-campaign-personalization-title d-flex justify-content-between align-items-start w-100 mx-0">
                                        <div className="d-flex flex-column">
                                            <div className="d-flex align-items-center gap-2">
                                            {isEditingName ? (
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    value={campaignData.campaign_name}
                                                    onChange={handleCampaignNameChange}
                                                    onBlur={handleCampaignNameBlur}
                                                    onKeyDown={handleCampaignNameKeyDown}
                                                    ref={campaignNameInputRef}
                                                    style={{
                                                        fontSize: '1.40rem',
                                                        fontWeight: 'bold',
                                                        width: 'auto',
                                                        minWidth: '180px'
                                                    }}
                                                />
                                            ) : (
                                                <OverlayTrigger
                                                    placement="bottom"
                                                    overlay={
                                                        <Tooltip id="campaign-title-description-tooltip" className="sxi-campaign-edit-tooltip single-line-tooltip" style={{marginLeft: '4rem'}}>
                                                            Configure your AI-powered personalization campaign
                                                        </Tooltip>
                                                    }
                                                >
                                                    <h2 style={{
                                                        marginBottom: 0,
                                                        fontSize: '1.40rem',
                                                        fontWeight: 'bold',
                                                        cursor: 'help',
                                                        color: '#052c65'
                                                    }}>{campaignData.campaign_name}</h2>
                                                </OverlayTrigger>
                                            )}
                                            <OverlayTrigger
                                                placement="top"
                                                overlay={
                                                    <Tooltip id="campaign-title-edit-tooltip" className="sxi-campaign-edit-tooltip">
                                                        Edit the title by clicking on the icon
                                                    </Tooltip>
                                                }
                                            >
                                                <button
                                                    className="btn btn-sm btn-link p-0"
                                                    onClick={handleCampaignNameEdit}
                                                >
                                                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                        <path d="M11 4H6.8C5.11984 4 4.27976 4 3.63803 4.32698C3.07354 4.6146 2.6146 5.07354 2.32698 5.63803C2 6.27976 2 7.11984 2 8.8V17.2C2 18.8802 2 19.7202 2.32698 20.362C2.6146 20.9265 3.07354 21.3854 3.63803 21.673C4.27976 22 5.11984 22 6.8 22H15.2C16.8802 22 17.7202 22 18.362 21.673C18.9265 21.3854 19.3854 20.9265 19.673 20.362C20 19.7202 20 18.8802 20 17.2V13M7.99997 16H9.67452C10.1637 16 10.4083 16 10.6385 15.9447C10.8425 15.8957 11.0376 15.8149 11.2166 15.7053C11.4184 15.5816 11.5914 15.4086 11.9373 15.0627L21.5 5.5C22.3284 4.67157 22.3284 3.32843 21.5 2.5C20.6716 1.67157 19.3284 1.67157 18.5 2.5L8.93723 12.0627C8.59133 12.4086 8.41838 12.5816 8.29469 12.7834C8.18504 12.9624 8.10423 13.1575 8.05523 13.3615C7.99997 13.5917 7.99997 13.8363 7.99997 14.3255V16Z" stroke="#AAAAAA" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                                    </svg>
                                                </button>
                                            </OverlayTrigger>
                                            </div>
                                        </div>
                                        <div className="d-flex align-items-center">
                                            <span className="campaign-tag-new me-2">{AB_CATEGORY}</span>
                                            <span className={`sxi-status-badge ab-header-status sxi-status-badge--${experimentStatusTone(campaignData.ab_test)}`}
                                                data-testid="ab-experiment-status">
                                                <span className="sxi-status-badge__dot" />
                                                <span className="sxi-status-badge__text">{experimentStatusLabel(campaignData.ab_test)}</span>
                                            </span>
                                        </div>
                                        </div>
                                    </Col>
                                </Row>
                            </Container>

                            {/* Split into Two Separate Accordions */}
                            <div className='main-accordion-body mt-4 p-2'>
                                <Accordion activeKey={activeAccordionKeys} onSelect={handleAccordionSelect}>
                                    {/* Landing Page Selection Accordion */}
                                    <Accordion.Item
                                        eventKey="0"
                                        style={landingPageSelected ? lockedAccordionStyle : undefined}
                                    >
                                        <Accordion.Header>
                                            <div className="d-flex align-items-center justify-content-between w-100">
                                                <div className="sxi-accordion-header-with-icon">
                                                    <div className="sxi-accordion-header-icon" aria-hidden="true">
                                                        <svg width="58" height="58" viewBox="0 0 58 58" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                            <g filter="url(#filter0_d_62_1564)">
                                                                <rect x="3" y="3" width="48" height="48" rx="6" fill="#E5EEFF" shapeRendering="crispEdges" />
                                                                <rect x="3" y="3" width="48" height="48" rx="6" stroke="#CEDEFF" shapeRendering="crispEdges" />
                                                                <path d="M28.0833 16.1667L18.4345 27.7452C18.0566 28.1986 17.8677 28.4254 17.8648 28.6168C17.8623 28.7833 17.9365 28.9417 18.066 29.0463C18.2149 29.1667 18.51 29.1667 19.1003 29.1667H26.9999L25.9166 37.8333L35.5654 26.2548C35.9432 25.8014 36.1322 25.5747 36.1351 25.3832C36.1376 25.2167 36.0634 25.0583 35.9339 24.9537C35.785 24.8333 35.4898 24.8333 34.8996 24.8333H26.9999L28.0833 16.1667Z" stroke="#2056C1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                            </g>
                                                            <defs>
                                                                <filter id="filter0_d_62_1564" x="0.5" y="0.5" width="57" height="57" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
                                                                    <feFlood floodOpacity="0" result="BackgroundImageFix" />
                                                                    <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha" />
                                                                    <feOffset dx="2" dy="2" />
                                                                    <feGaussianBlur stdDeviation="2" />
                                                                    <feComposite in2="hardAlpha" operator="out" />
                                                                    <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.08 0" />
                                                                    <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_62_1564" />
                                                                    <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_62_1564" result="shape" />
                                                                </filter>
                                                            </defs>
                                                        </svg>
                                                    </div>
                                                    <LockedHeader
                                                        label="Select Landing Page"
                                                        locked={landingPageSelected}
                                                        reason="Landing page is already selected"
                                                        additionalContent={landingPageSelected ? `Selected Landing Page URL: ${campaignData.landing_page_urls}` : null}
                                                    />
                                                </div>
                                                <div className="d-flex align-items-center gap-2">
                                                    {landingPageSelected && <AccordionLockIcon reason="Landing page is already selected" />}
                                                    <span className={`campaign-accordion-chevron ${activeAccordionKeys.includes('0') ? 'open' : ''}`} aria-hidden="true">
                                                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                                            <path d="M4.5 6L8 9.5L11.5 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                                        </svg>
                                                    </span>
                                                </div>
                                            </div>
                                        </Accordion.Header>
                                        <Accordion.Body>
                                            <Container fluid className="px-0">
                                                <Row className="mb-2 mt-3">
                                                    <Col xs={12} md={8} className="mb-3 mb-md-0">
                                                        {/* <Label className="charts_filter_label mt-2">Landing page URL</Label> */}
                                                        <input
                                                            type="text"
                                                            className={`form-control ${landingPageValidationError ? 'is-invalid' : ''}`}
                                                            value={campaignData?.landing_page_urls || ''}
                                                            onChange={handleLandingPageInputChange}
                                                            placeholder="Landing page URL"
                                                            disabled={landingPageSelected}
                                                            />
                                                    </Col>
                                                    <Col xs={12} md={4} className="d-flex align-items-end">
                                                        {select_landing_page_button_loading ? (
                                                            <Button className="sxi_primary_blue_btn" disabled>
                                                                <Spinner size="sm" animation="border"/>
                                                            </Button>
                                                        ) : (
                                                            <Button 
                                                                onClick={handleLandingPageSelect} 
                                                                className="sxi_primary_blue_btn btn-height" 
                                                                disabled={
                                                                    landingPageSelected ||
                                                                    !campaignData.landing_page_urls ||
                                                                    !campaignData.landing_page_urls.trim() ||
                                                                    Boolean(landingPageValidationError)
                                                                }
                                                            >
                                                                Select
                                                            </Button>
                                                        )}
                                                    </Col>
                                                    
                                                    {landingPageValidationError && (
                                                        <div className="invalid-feedback d-block">
                                                            {landingPageValidationError}
                                                        </div>
                                                    )}
                                                </Row>
                                                <div style={{fontSize: '0.95rem'}}>
                                                    Note: Once selected, the landing page cannot be changed.
                                                </div>
                                            </Container>
                                        </Accordion.Body>
                                    </Accordion.Item>

                                    {/* SDK verification banner — sits between accordions so it stays
                                        visible after the landing-page accordion auto-collapses on select. */}
                                    {landingPageSelected && sdkVerifyState !== 'verified' && (
                                        <div
                                            role="status"
                                            aria-live="polite"
                                            style={{
                                                margin: '12px 0',
                                                border: `1px solid ${sdkVerifyState === 'failed' ? '#f5c2c7' : '#ffe69c'}`,
                                                background: sdkVerifyState === 'failed' ? '#fdecea' : '#fff8e1',
                                                borderRadius: '6px',
                                                overflow: 'hidden'
                                            }}
                                        >
                                            {/* Compact header — collapsed by default; click to reveal the full message */}
                                            <button
                                                type="button"
                                                onClick={() => setSdkNoticeOpen(o => !o)}
                                                aria-expanded={sdkNoticeOpen}
                                                style={{
                                                    width: '100%',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '8px',
                                                    padding: '6px 12px',
                                                    background: 'transparent',
                                                    border: 'none',
                                                    cursor: 'pointer',
                                                    textAlign: 'left'
                                                }}
                                            >
                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                                                    <path d="M12 3L22 20H2L12 3Z" stroke={sdkVerifyState === 'failed' ? '#b91c1c' : '#b88600'} strokeWidth="1.6" strokeLinejoin="round"/>
                                                    <path d="M12 10V14" stroke={sdkVerifyState === 'failed' ? '#b91c1c' : '#b88600'} strokeWidth="1.6" strokeLinecap="round"/>
                                                    <circle cx="12" cy="17" r="1" fill={sdkVerifyState === 'failed' ? '#b91c1c' : '#b88600'}/>
                                                </svg>
                                                <span style={{flex: 1, minWidth: 0, fontSize: '0.78rem', fontWeight: 600, color: sdkVerifyState === 'failed' ? '#7f1d1d' : '#6b5300'}}>
                                                    {sdkVerifyState === 'failed'
                                                        ? 'SDK verification failed'
                                                        : 'SDK connection not verified'}
                                                </span>
                                                <span className={`campaign-accordion-chevron ${sdkNoticeOpen ? 'open' : ''}`} aria-hidden="true">
                                                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                                        <path d="M4.5 6L8 9.5L11.5 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                                    </svg>
                                                </span>
                                            </button>

                                            {/* Full message + verify action — revealed on expand */}
                                            {sdkNoticeOpen && (
                                                <div style={{padding: '0 12px 12px 12px', display: 'flex', alignItems: 'flex-start', gap: '12px'}}>
                                                    <div style={{flex: 1, minWidth: 0, fontSize: '0.9rem', color: '#555'}}>
                                                        {sdkVerifyState === 'failed' && sdkVerifyError
                                                            ? sdkVerifyError
                                                            : "The SDK may not be present on your website. If you're confident it's installed, you can still create the campaign — but the variations won't be shown to visitors if the SDK isn't present."}
                                                    </div>
                                                    <div style={{flex: '0 0 auto'}}>
                                                        <Button
                                                            size="sm"
                                                            onClick={handleSdkVerifyClick}
                                                            disabled={sdkVerifyState === 'verifying'}
                                                            className="sxi_primary_blue_btn"
                                                        >
                                                            {sdkVerifyState === 'verifying' ? (
                                                                <>
                                                                    <Spinner size="sm" animation="border" style={{marginRight: '6px'}}/>
                                                                    Verifying…
                                                                </>
                                                            ) : sdkVerifyState === 'failed' ? 'Retry verification' : 'Verify connection'}
                                                        </Button>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {/* Template Selection Accordion */}
                                    <Accordion.Item
                                        eventKey="1"
                                        style={templateAccordionLocked ? lockedAccordionStyle : undefined}
                                    >
                                        <Accordion.Header>
                                            <div className="d-flex align-items-center justify-content-between w-100">
                                                <div className="sxi-accordion-header-with-icon">
                                                    <div className="sxi-accordion-header-icon" aria-hidden="true">
                                                        <svg width="58" height="58" viewBox="0 0 58 58" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                            <g filter="url(#filter0_d_62_1564)">
                                                                <rect x="3" y="3" width="48" height="48" rx="6" fill="#E5EEFF" shapeRendering="crispEdges" />
                                                                <rect x="3" y="3" width="48" height="48" rx="6" stroke="#CEDEFF" shapeRendering="crispEdges" />
                                                                <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" transform="translate(17 17)" stroke="#2056C1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                                                            </g>
                                                            <defs>
                                                                <filter id="filter0_d_62_1564" x="0.5" y="0.5" width="57" height="57" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
                                                                    <feFlood floodOpacity="0" result="BackgroundImageFix" />
                                                                    <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha" />
                                                                    <feOffset dx="2" dy="2" />
                                                                    <feGaussianBlur stdDeviation="2" />
                                                                    <feComposite in2="hardAlpha" operator="out" />
                                                                    <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.08 0" />
                                                                    <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_62_1564" />
                                                                    <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_62_1564" result="shape" />
                                                                </filter>
                                                            </defs>
                                                        </svg>
                                                    </div>
                                                    <LockedHeader
                                                        label="Select Template"
                                                        locked={templateAccordionLocked}
                                                        reason="Template is already selected"
                                                    />
                                                </div>
                                                <div className="d-flex align-items-center gap-2">
                                                    {templateAccordionLocked && <AccordionLockIcon reason="Template is already selected" />}
                                                    <span className={`campaign-accordion-chevron ${activeAccordionKeys.includes('1') ? 'open' : ''}`} aria-hidden="true">
                                                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                                            <path d="M4.5 6L8 9.5L11.5 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                                        </svg>
                                                    </span>
                                                </div>
                                            </div>
                                        </Accordion.Header>
                                        <Accordion.Body className='px-0'>
                                            <Container fluid className="px-0">
                                                {!landingPageSelected ? (
                                                    <div className="py-4">
                                                        <div>
                                                            Please select a landing page first to work with templates.
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <>
                                                        <Row className="mb-3 px-4">
                                                            <Col className="d-flex flex-wrap mt-3 gap-2">
                                                                <Button
                                                                    className="sxi_primary_blue_btn btn-height"
                                                                    onClick={handleLaunchVisualEditor}
                                                                    disabled={!canLaunchVisualEditor}
                                                                >
                                                                    Launch Visual Editor
                                                                </Button>
                                                                <Button
                                                                    className='back-button btn-height'
                                                                    // variant="outline-secondary"
                                                                    onClick={handleTemplateRefresh}
                                                                    disabled={templateRefreshLoading}
                                                                >
                                                                    {templateRefreshLoading ? (
                                                                        <>
                                                                            <Spinner size="sm" animation="border" className="me-2"/>
                                                                            Refreshing...
                                                                        </>
                                                                    ) : (
                                                                        'Refresh Template'
                                                                    )}
                                                                </Button>
                                                            </Col>
                                                        </Row>
                                                        <div className=" mb-3 px-4" style={{fontSize: '0.9rem'}}>
                                                            No template data yet. Launch the editor, make your changes, then click Refresh.
                                                        </div>
                                                        {templatePreview ? (
                                                            <>
                                                                <div className="mt-3 personalization-targeting-table">
                                                                    <Table responsive className="mb-0">
                                                                        <thead>
                                                                            <tr>
                                                                                <th className="channel_performance_header_cell">Label</th>
                                                                                <th className="channel_performance_header_cell">Type</th>
                                                                                <th className="channel_performance_header_cell">View</th>
                                                                                <th className="channel_performance_header_cell">Selector</th>
                                                                                <th className="channel_performance_header_cell">Value</th>
                                                                            </tr>
                                                                        </thead>
                                                                        <tbody>
                                                                            {getCustomizedSections(templatePreview).length === 0 ? (
                                                                                <tr className="channel_performance_table_row">
                                                                                    <td colSpan={5} className="channel_performance_table_cell text-center">
                                                                                        No customized sections were returned. Make sure you saved from the Visual Editor and try refreshing again.
                                                                                    </td>
                                                                                </tr>
                                                                            ) : (
                                                                                getCustomizedSections(templatePreview).map((section, index) => (
                                                                                    <tr
                                                                                        key={`${section.selector}-${index}`}
                                                                                        className={index % 2 === 0 ? 'channel_performance_table_row' : 'channel_performance_old_table_row'}
                                                                                    >
                                                                                        <td className="channel_performance_table_cell">{section.label || '-'}</td>
                                                                                        <td className="channel_performance_table_cell">{section.type || '-'}</td>
                                                                                        <td className="channel_performance_table_cell">{section.view || '-'}</td>
                                                                                        <td className="channel_performance_table_cell">{section.selector || '-'}</td>
                                                                                        <td className="channel_performance_table_cell" style={{whiteSpace: 'pre-wrap'}}>{section.value || '-'}</td>
                                                                                    </tr>
                                                                                ))
                                                                            )}
                                                                        </tbody>
                                                                    </Table>
                                                                </div>
                                                                <div className="px-4" style={{fontSize: '0.9rem'}}>
                                                                    Review the updates above. Once selected, the template for this campaign cannot be changed.
                                                                </div>
                                                                <div className="mt-3 px-4">
                                                                {select_template_button_loading ? (
                                                                    <Button className="sxi_primary_blue_btn" disabled>
                                                                        <Spinner size="sm" animation="border"/>
                                                                    </Button>
                                                                ) : (
                                                                    <Button 
                                                                        onClick={handleTemplateSelect} 
                                                                        className="sxi_primary_blue_btn btn-height" 
                                                                            disabled={templateAccordionLocked}
                                                                    >
                                                                        Select Template
                                                                    </Button>
                                                                )}
                                                        </div>
                                                            </>
                                                        ) : (''
                                                            // <div className="text-center py-4">
                                                            //     <em>No template data yet. Launch the editor, make your changes, then click Refresh.</em>
                                                            // </div>
                                                        )}
                                                    </>
                                                )}
                                            </Container>
                                        </Accordion.Body>
                                    </Accordion.Item>

                                    {/* A/B Test Accordion */}
                                    <Accordion.Item eventKey="2">
                                        <Accordion.Header>
                                            <div className="d-flex align-items-center justify-content-between w-100">
                                                <div className="sxi-accordion-header-with-icon">
                                                    <div className="sxi-accordion-header-icon" aria-hidden="true">
                                                        <svg width="58" height="58" viewBox="0 0 58 58" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                            <g filter="url(#filter0_d_67_2322)">
                                                                <rect x="3" y="3" width="48" height="48" rx="6" fill="#E5EEFF" shapeRendering="crispEdges" />
                                                                <rect x="3" y="3" width="48" height="48" rx="6" stroke="#CEDEFF" shapeRendering="crispEdges" />
                                                                <path d="M34.414 22.5312C34.2652 24.5965 32.7332 26.1875 31.0625 26.1875C29.3918 26.1875 27.8571 24.597 27.7109 22.5312C27.5586 20.3827 29.05 18.875 31.0625 18.875C33.0749 18.875 34.5664 20.4218 34.414 22.5312Z" stroke="#2056C1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                                <path d="M31.0623 29.4375C27.7529 29.4375 24.5704 31.0813 23.7732 34.2825C23.6675 34.7061 23.9331 35.125 24.3683 35.125H37.7568C38.192 35.125 38.456 34.7061 38.3519 34.2825C37.5547 31.03 34.3722 29.4375 31.0623 29.4375Z" stroke="#2056C1" strokeWidth="2" strokeMiterlimit="10" />
                                                                <path d="M24.1564 23.4423C24.0375 25.0916 22.7995 26.3906 21.465 26.3906C20.1304 26.3906 18.8904 25.0921 18.7736 23.4423C18.6522 21.7264 19.8572 20.5 21.465 20.5C23.0727 20.5 24.2777 21.7579 24.1564 23.4423Z" stroke="#2056C1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                                <path d="M24.4609 29.5391C23.5443 29.1191 22.5348 28.9576 21.4649 28.9576C18.8242 28.9576 16.2801 30.2703 15.6428 32.8271C15.559 33.1653 15.7713 33.5 16.1186 33.5H21.8203" stroke="#2056C1" strokeWidth="2" strokeMiterlimit="10" strokeLinecap="round" />
                                                            </g>
                                                            <defs>
                                                                <filter id="filter0_d_67_2322" x="0.5" y="0.5" width="57" height="57" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
                                                                    <feFlood floodOpacity="0" result="BackgroundImageFix" />
                                                                    <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha" />
                                                                    <feOffset dx="2" dy="2" />
                                                                    <feGaussianBlur stdDeviation="2" />
                                                                    <feComposite in2="hardAlpha" operator="out" />
                                                                    <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.08 0" />
                                                                    <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_67_2322" />
                                                                    <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_67_2322" result="shape" />
                                                                </filter>
                                                            </defs>
                                                        </svg>
                                                    </div>
                                                    <span>A/B Test</span>
                                                </div>
                                                <span className={`campaign-accordion-chevron ${activeAccordionKeys.includes('2') ? 'open' : ''}`} aria-hidden="true">
                                                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                                        <path d="M4.5 6L8 9.5L11.5 6" stroke="black" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                                    </svg>
                                                </span>
                                            </div>
                                        </Accordion.Header>
                                        <Accordion.Body>
                                            <ABTestSection
                                                app_url={app_url}
                                                campaignData={campaignData}
                                                saveData={saveData}
                                                templateLocked={templateAccordionLocked}
                                                allTargeting={initialallTargeting}
                                            />
                                        </Accordion.Body>
                                    </Accordion.Item>
                                </Accordion>
                            </div>

                            {/* Original Publish Button Area */}
                            <div className='mx-2 mt-3'>
                                <div className="d-flex align-items-center gap-2">
                                    {publish_btn_loading ?
                                        <Button
                                            className='d-flex align-items-center justify-content-center sxi_primary_blue_btn btn-height'
                                            style={{marginBottom: '4px'}} disabled><Spinner size="sm"
                                                                                            animation="border"/></Button>
                                        :
                                        <Button
                                            className='d-flex align-items-center justify-content-center sxi_primary_blue_btn btn-height'
                                            disabled={!landingPageSelected || !campaignData?.landing_page_urls?.trim() || campaignSnapshot(campaignData) === initialSnapshotRef.current}
                                            onClick={handlePublish}>Save</Button>
                                    }
                                    <button className="back-button btn-height" onClick={handleBack}>
                                        {/* <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                                            <path fillRule="evenodd" d="M15 8a.5.5 0 0 0-.5-.5H2.707l3.147-3.146a.5.5 0 1 0-.708-.708l-4 4a.5.5 0 0 0 0 .708l4 4a.5.5 0 0 0 .708-.708L2.707 8.5H14.5A.5.5 0 0 0 15 8z" />
                                        </svg> */}
                                        Back
                                    </button>
                                </div>
                            </div>
                        </>
                    )}
                </>
            )}
        </div>
    );
};

export default ABTestingCampaign;

// Function to clean URL by removing parameters and trailing slashes
const cleanUrl = (url) => {
    if (!url) return '';
    
    try {
        // Create URL object to parse the URL
        const urlObj = new URL(url);
        
        // Get only the origin and pathname (remove query parameters, hash, etc.)
        let cleanUrl = urlObj.origin + urlObj.pathname;
        
        // Remove trailing slash if it exists
        cleanUrl = cleanUrl.replace(/\/$/, '');
        
        return cleanUrl;
    } catch (error) {
        // If URL parsing fails, try to clean manually
        let cleanUrl = url;
        
        // Remove query parameters (everything after ?)
        cleanUrl = cleanUrl.split('?')[0];
        
        // Remove hash (everything after #)
        cleanUrl = cleanUrl.split('#')[0];
        
        // Remove trailing slash
        cleanUrl = cleanUrl.replace(/\/$/, '');
        
        return cleanUrl;
    }
};

function getNameFromUrl(url) {
    try {
        const parsedUrl = new URL(url);
        let pathname = parsedUrl.pathname.replace(/\/+$/, ""); // remove trailing slash
    
        if (!pathname || pathname === "/") {
            // If no path, return domain without www and TLD
            let hostname = parsedUrl.hostname.replace(/^www\./, "");
            let name = hostname.split(".")[0]; // first part before TLD
            return name;
        }
    
        // Get last path segment
        let parts = pathname.split("/");
        let lastPart = parts.pop() || parts.pop(); // handle trailing slash
    
        // Replace underscores/dashes with spaces
        let cleanName = lastPart.replace(/[-_]/g, " ");
    
        return cleanName.trim();
    } catch (e) {
        return null;
    }
}  